import type { Command } from '@/core/Command';
import type { FloraInstance } from '@/flora/FloraInstance';
import type { FloraWorld } from '@/flora/FloraWorld';

/** Deep-ish clone of an instance's mutable value fields (positions/rotations are copied). */
function snapshot(instance: FloraInstance): FloraInstance {
  return {
    ...instance,
    position: { ...instance.position },
    rotation: { ...instance.rotation },
  };
}

/** Add a single placed plant (spec §3.5). */
export class AddFloraCommand implements Command {
  readonly label = 'Place plant';
  readonly kind = 'plant' as const;
  private readonly instance: FloraInstance;

  constructor(
    private readonly world: FloraWorld,
    instance: FloraInstance,
  ) {
    this.instance = snapshot(instance);
  }

  execute(): void {
    this.world.add(snapshot(this.instance));
  }

  undo(): void {
    this.world.remove(this.instance.id);
  }
}

/** Add several plants at once (duplicate/paste, spec §4.4). */
export class AddManyFloraCommand implements Command {
  readonly label: string;
  readonly kind = 'plant' as const;
  private readonly instances: FloraInstance[];

  constructor(
    private readonly world: FloraWorld,
    instances: FloraInstance[],
    label?: string,
  ) {
    this.instances = instances.map(snapshot);
    this.label = label ?? (instances.length === 1 ? 'Duplicate plant' : `Duplicate ${instances.length} plants`);
  }

  execute(): void {
    for (const inst of this.instances) {
      this.world.add(snapshot(inst));
    }
  }

  undo(): void {
    for (const inst of this.instances) {
      this.world.remove(inst.id);
    }
  }
}

/** Remove one or more plants (spec §3.6). */
export class RemoveFloraCommand implements Command {
  readonly label: string;
  readonly kind = 'remove' as const;
  private readonly removed: FloraInstance[];

  constructor(
    private readonly world: FloraWorld,
    instances: FloraInstance[],
  ) {
    this.removed = instances.map(snapshot);
    this.label = instances.length === 1 ? 'Delete plant' : `Delete ${instances.length} plants`;
  }

  execute(): void {
    for (const inst of this.removed) {
      this.world.remove(inst.id);
    }
  }

  undo(): void {
    for (const inst of this.removed) {
      this.world.add(snapshot(inst));
    }
  }
}

export interface FloraChange {
  before: FloraInstance;
  after: FloraInstance;
}

/**
 * Apply a set of before→after instance changes (move/rotate/maturity). Used for the interactive
 * TransformTool, which applies changes live and records this at the end so a whole drag is one undo
 * step. A `mergeKey` lets successive slider/drag commands in the *same* interaction collapse.
 */
export class TransformFloraCommand implements Command {
  readonly label: string;
  readonly kind = 'transform' as const;
  readonly mergeKey?: string;
  private changes: Map<string, FloraChange>;

  constructor(
    private readonly world: FloraWorld,
    changes: Iterable<FloraChange>,
    label = 'Transform plant',
    mergeKey?: string,
  ) {
    this.label = label;
    if (mergeKey !== undefined) this.mergeKey = mergeKey;
    this.changes = new Map();
    for (const change of changes) {
      this.changes.set(change.before.id, {
        before: snapshot(change.before),
        after: snapshot(change.after),
      });
    }
  }

  execute(): void {
    for (const { after } of this.changes.values()) {
      if (this.world.has(after.id)) this.world.update(snapshot(after), { rebuild: true });
    }
  }

  undo(): void {
    for (const { before } of this.changes.values()) {
      if (this.world.has(before.id)) this.world.update(snapshot(before), { rebuild: true });
    }
  }

  mergeWith(next: Command): boolean {
    if (!(next instanceof TransformFloraCommand)) return false;
    // Keep our original `before`, adopt the newer `after` for each affected instance.
    for (const [id, change] of next.changes) {
      const existing = this.changes.get(id);
      if (existing) {
        existing.after = snapshot(change.after);
      } else {
        this.changes.set(id, {
          before: snapshot(change.before),
          after: snapshot(change.after),
        });
      }
    }
    return true;
  }
}
